/**
 * Modale di warning a T-2 minuti dall'auto-logout.
 *
 * Mostra countdown da `secondsRemaining` a 0. Bottoni:
 * - "Rimani connesso": chiama onStay
 * - "Disconnetti ora": chiama onSignOutNow
 */

import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

export interface SessionTimeoutWarningProps {
  isOpen: boolean;
  secondsRemaining: number;
  onStay: () => void;
  onSignOutNow: () => void;
}

export function SessionTimeoutWarning({
  isOpen,
  secondsRemaining,
  onStay,
  onSignOutNow,
}: SessionTimeoutWarningProps) {
  const [remaining, setRemaining] = useState(secondsRemaining);

  useEffect(() => {
    if (!isOpen) return;
    // Re-inizializza il countdown ogni volta che la modale si apre: il
    // setState qui riallinea lo stato locale al prop `secondsRemaining`
    // ed è il pattern voluto.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRemaining(secondsRemaining);
    const id = setInterval(() => {
      setRemaining((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [isOpen, secondsRemaining]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onStay()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content
          aria-describedby="session-timeout-desc"
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md bg-white rounded-xl shadow-xl p-6 md:p-8 focus:outline-none"
        >
          <Dialog.Title className="font-headline-md text-headline-md text-text-main mb-2">
            Sessione in scadenza
          </Dialog.Title>
          <Dialog.Description
            id="session-timeout-desc"
            className="font-body-md text-text-secondary mb-6"
          >
            Per la tua sicurezza verrai disconnesso tra{' '}
            <strong aria-live="polite">{remaining}</strong> secondi.
            Premi "Rimani connesso" per continuare.
          </Dialog.Description>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onSignOutNow}
              className="flex-1 h-12 rounded-lg border border-border-gray font-body-md font-semibold text-on-surface hover:bg-surface-container-low"
            >
              Disconnetti ora
            </button>
            <button
              type="button"
              onClick={onStay}
              className="flex-1 h-12 rounded-lg bg-primary text-on-primary font-body-md font-bold hover:opacity-90"
            >
              Rimani connesso
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
